/**
 * How much weight a match score deserves, said in words.
 *
 * **A percentage on its own misleads here, and we know by how much.** The AI
 * scores every match out of one, and on a real library of seventeen roles the
 * scores came back like this:
 *
 *   0.90, 0.88, 0.78   the three matches that are plainly right
 *   0.72               ten of them, right and wrong mixed together
 *   0.70, 0.62         the three weakest
 *
 * 0.72 is not a judgement. It is where this model parks when it is not sure,
 * and it does not move: told the pass mark was 0.6 it scored 0.72, and with
 * the pass mark removed from its instructions entirely it scored 0.72 again.
 *
 * So a reader seeing "Match 72%" beside a bar that is three quarters full is
 * being told the match is fairly good, when what actually happened is that
 * the AI shrugged. The number stays, because a person comparing two matches
 * needs it, and these words go beside it.
 *
 * The boundary is 0.75, which is where the matches that are plainly right
 * separate from the ones the AI was unsure about. It is drawn from that one
 * library and should be looked at again when there is more data.
 */

/** What a reader should do about a match, which is the useful question. */
export type Weight = 'confident' | 'unsure' | 'weak'

export interface HowSure {
  weight: Weight
  /** One word for the badge beside the number. */
  label: string
  /** A sentence for a reader who stops on it. Empty where there is nothing to add. */
  says: string
}

/** Above this the AI was actually confident rather than hedging. */
export const SURE_ABOVE = 0.75

/** At or below this it is the weakest thing the run was willing to offer. */
export const WEAK_AT_OR_BELOW = 0.7

export function howSure(score: number | undefined | null): HowSure {
  const n = typeof score === 'number' && Number.isFinite(score) ? score : 0

  if (n > SURE_ABOVE) {
    return {
      weight: 'confident',
      label: 'Confident',
      says: '',
    }
  }

  if (n <= WEAK_AT_OR_BELOW) {
    return {
      weight: 'weak',
      label: 'Weak',
      says: 'The AI had little confidence in this one. Read what it says and expect to disagree.',
    }
  }

  /*
   * The middle, and the one that needed saying. A reader who skims sees a
   * number in the seventies and moves on, which is exactly the match most
   * worth stopping on.
   */
  return {
    weight: 'unsure',
    label: 'Check this',
    says: 'The AI was not sure. Scores in this range have been both right and wrong, so read the reasoning before you rely on the figures.',
  }
}
